import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero, { SectionHeading } from "../components/PageHero";

const values = [
  "Catholic faith as the foundation",
  "Serious academics without busyness",
  "Latin and Classical Literature",
  "Reverent sacramental life",
  "Limited technology in childhood",
  "Active parental partnership",
];

const tuition = [
  {
    key: "academic",
    name: "Academic Core",
    days: "3 Days",
    subtitle: "Three Days Per Week",
    price: "$5,625",
  },
  {
    key: "enrichment",
    name: "Enrichment & Formation",
    days: "2 Days",
    subtitle: "Two Days Per Week",
    price: "$2,950",
  },
  {
    key: "full",
    name: "Complete Program",
    days: "5 Days",
    subtitle: "Monday–Friday",
    price: "$8,575",
    featured: true,
  },
];

const steps = [
  { n: "01", title: "Inquire", body: "Complete the founding-family inquiry form." },
  { n: "02", title: "Schedule a Call", body: "Schedule a call with school leadership to discuss your family." },
  { n: "03", title: "Apply", body: "Submit an application for each student." },
  { n: "04", title: "Family Interview", body: "Ensure a good fit for both family and Academy." },
  { n: "05", title: "Enroll", body: "Submit agreement and deposit to secure placement." },
];

const Admissions = () => {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Tuition & Admissions"
        headline={<>Become a <em className="not-italic text-[#c9a961]">Founding Family</em>.</>}
      />

      {/* Values */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 pt-8 md:pt-10 pb-20">
          <SectionHeading eyebrow="Who Is Angel of Peace For?" title="Families Who Value…" hideDivider />
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 max-w-[820px] mx-auto">
            {values.map((v, i) => (
              <li key={v} className="flex items-start gap-4 font-serif-body text-[16px] text-[#1e2952]">
                <span className="font-sans-nav text-[10px] tracking-[0.2em] text-[#c9a961] mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Tuition & Enrollment Options */}
      <section className="bg-[#f6efd8]/40 border-y border-[#eae1c4]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14 py-20">
          <SectionHeading eyebrow="Tuition & Enrollment Options" title="Flexible Family Enrollment" />
          <p className="font-serif-body text-center max-w-[820px] mx-auto text-[16.5px] leading-[1.85] text-[#3a466e] -mt-6 mb-14">
            Families may enroll in the complete five-day program or choose an alternative schedule that best suits your individual family's needs.
          </p>

          {/* Three-card summary — Complete Program centered & prominent */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1000px] mx-auto items-stretch">
            {tuition.map((t) => (
              <div
                key={t.key}
                data-testid={`tuition-card-${t.key}`}
                className={`flex flex-col text-center p-8 ${
                  t.featured
                    ? "bg-[#1e2952] text-[#fbf7ee] border border-[#c9a961] md:scale-[1.04] md:shadow-[0_0_0_1px_#c9a961]"
                    : "bg-[#fbf7ee] border border-[#e6dfc8]"
                }`}
              >
                <div className={`font-sans-nav text-[10.5px] tracking-[0.32em] uppercase mb-3 ${t.featured ? "text-[#c9a961]" : "text-[#c9a961]"}`}>
                  {t.days}
                </div>
                <h3 className="font-display text-[22px] md:text-[24px] mb-4" style={{ fontWeight: 500 }}>
                  {t.name}
                </h3>
                <div className="font-display text-[36px] md:text-[40px] mb-1 leading-none" style={{ fontWeight: 600 }}>
                  {t.price}
                  {t.asterisk && <sup className="text-[#c9a961] text-[20px] ml-0.5">*</sup>}
                </div>
                <div className={`font-sans-nav text-[10.5px] tracking-[0.24em] uppercase ${t.featured ? "text-[#c9a961]" : "text-[#8a7a4e]"}`}>
                  per year
                </div>
                {t.description && (
                  <p className="font-serif-body text-[14px] leading-[1.7] italic text-[#8a7a4e] mt-5 pt-5 border-t border-[#eae1c4]">
                    {t.description}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* University-Style Course Enrollment */}
          <div className="max-w-[880px] mx-auto mt-12 pt-10 border-t border-dotted border-[#c9a961]/50 text-center" data-testid="university-style-enrollment">
            <div className="font-sans-nav text-[11px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">University-Style Course Enrollment</div>
            <p className="font-serif-body text-[15.5px] md:text-[16px] leading-[1.85] text-[#3a466e] m-0">
              All courses may be available for individual enrollment as space permits.{" "}
              <Link to="/inquire" className="text-[#c9a961] hover:text-[#1e2952] underline decoration-[#c9a961]/60 underline-offset-2 transition-colors">
                Inquire for more information.
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Tuition Assistance & Scholarships */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14 py-20 text-center">
          <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">Tuition Assistance &amp; Scholarships</div>
          <h2 className="font-display text-[#1e2952] text-[28px] md:text-[36px]" style={{ fontWeight: 500 }}>
            An Accessible Catholic Education.
          </h2>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-8 space-y-5 font-serif-body text-[16.5px] leading-[1.85] text-[#3a466e] text-left">
            <p>
              Angel of Peace is committed to making a faithful Catholic liberal arts education accessible to families who share the mission of the school. Additional assistance may be available for larger families, along with a limited number of need-based scholarships.
            </p>
            <p>
              We encourage all interested families to apply and to indicate any request for assistance during the admissions process, without letting the tuition rates alone prevent them from beginning the process.
            </p>
          </div>

          {/* TEFA note */}
          <div className="max-w-[820px] mx-auto mt-12 pt-10 border-t border-dotted border-[#c9a961]/50 text-left">
            <div className="font-sans-nav text-[11px] tracking-[0.32em] uppercase text-[#c9a961] mb-4 text-center">Texas Education Freedom Accounts (TEFA)</div>
            <div className="space-y-5 font-serif-body text-[16px] leading-[1.85] text-[#3a466e]">
              <p>
                Angel of Peace Academy will apply to be a vendor to receive homeschool funds for up to <span className="font-display italic text-[#1e2952]">$2,000</span> toward course enrollment and school fees.
              </p>
              <p>
                Angel of Peace plans to pursue the accreditation necessary for eligibility in the TEFA private-school network during our second school year, allowing for full private-school funds to hopefully be used toward tuition at Angel of Peace Academy beginning in our <span className="font-display italic text-[#1e2952]">third school year</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 pt-6 md:pt-8 pb-20">
          <SectionHeading eyebrow="Admissions Process" title="Five Steps. One Conversation." />
          <div className="space-y-4">
            {steps.map((s) => (
              <div key={s.n} className="flex items-start gap-6 border-b border-[#eae1c4] pb-4">
                <div className="font-display text-[36px] text-[#c9a961] leading-none w-14 shrink-0" style={{ fontWeight: 500 }}>{s.n}</div>
                <div>
                  <h4 className="font-display text-[#1e2952] text-[20px] mb-1" style={{ fontWeight: 600 }}>{s.title}</h4>
                  <p className="font-serif-body text-[15px] leading-[1.6] text-[#3a466e]">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Families (combined) */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14 py-20 text-center">
          <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">Founding Families · Parent Partnership</div>
          <h2 className="font-display text-[30px] md:text-[38px]" style={{ fontWeight: 500 }}>Help Build the Culture From the Beginning.</h2>
          <div className="mt-5 flex items-center justify-center gap-3"><span className="block h-px w-40 bg-[#c9a961]"></span><span className="text-[#c9a961]">†</span><span className="block h-px w-40 bg-[#c9a961]"></span></div>
          <p className="font-serif-body mt-8 text-[16.5px] leading-[1.8] text-[#e8e2cf]">
            Parents are the primary educators. Angel of Peace exists to assist—not replace—the family. We seek founding families who will enroll their children and help build a community of fidelity, friendship, and joy.
          </p>
          <div className="mt-10 flex justify-center">
            <Link to="/inquire" className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 bg-[#c9a961] text-[#1e2952] hover:bg-[#e2c98a] transition-colors">
              Inquire Today
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Admissions;
